import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Dropzone from 'components/Dropzone';
import ListingContext from '../../providers/Listing/Listing.context';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import DraggableCard from 'components/DraggableCard';
import { getImgUrl } from 'components/ResolveImage/utils';
import { Button } from 'ncoded-component-library';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './PortfolioGallery.styles.scss';
import './PortfolioGallery.styles.responsive.scss';

type PortfolioGalleryProps = {
  className?: string;
};

const PortfolioGallery: React.FC<PortfolioGalleryProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);
  const {
    listing,
    gallery,
    isUploading,
    updateGallery,
    uploadListingFile,
    deleteListingFile,
  } = useContext(ListingContext);
  const [images, setImages] = useState(gallery);
  const [isReordering, setIsReordering] = useState(false);

  const classes = classNames('portfolio-gallery', className);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;

    if (active.id === over.id) return;

    setImages((images) => {
      const oldIndex = images.findIndex(({ _id }) => _id === active.id);
      const newIndex = images.findIndex(({ _id }) => _id === over.id);

      return arrayMove(images, oldIndex, newIndex);
    });
  };

  const updateOrder = useCallback(() => {
    try {
      const fileIds = images.map(({ _id }) => _id);
      api.listings.reorderGallery(listing._id, fileIds);
    } catch (e) {
      popServerError(e);
    }
  }, [images, listing._id, popServerError]);

  const handleOnReorder = useCallback(() => {
    if (isReordering) {
      updateGallery(images);
      updateOrder();
    }

    setIsReordering((prev) => !prev);
  }, [images, isReordering, updateGallery, updateOrder]);

  const RenderDropzone = useMemo(
    () => (
      <Dropzone
        accept="image/*"
        fileName="gallery"
        files={gallery}
        multiple
        sequentialUpload
        uploadRequest={uploadListingFile}
        deleteRequest={deleteListingFile}
      />
    ),
    [deleteListingFile, gallery, uploadListingFile],
  );

  useEffect(() => {
    setImages(gallery);
  }, [gallery]);

  const RenderReorder = useMemo(
    () => (
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={images}
          strategy={horizontalListSortingStrategy}
        >
          {images.map((img) => (
            <DraggableCard key={img._id} id={img._id}>
              <img src={getImgUrl(img.url, '720')} alt="Gallery" />
            </DraggableCard>
          ))}
        </SortableContext>
      </DndContext>
    ),
    [images],
  );

  return (
    <div className={classes}>
      <div className="portfolio-gallery__heading">
        <p>{t('editGallery')}</p>
        {gallery?.length > 1 && (
          <Button
            variant="outline"
            onClick={handleOnReorder}
            disabled={!isReordering && isUploading}
          >
            {t(isReordering ? 'done' : 'reorder')}
          </Button>
        )}
      </div>
      <div className="portfolio-gallery__dropzone-container">
        {isReordering ? RenderReorder : RenderDropzone}
      </div>
    </div>
  );
};

export default PortfolioGallery;
